/**
 * Who's On Duty page — Shows fire stations with their apparatus and
 * currently assigned members.
 *
 * Stations: FS10 - Dime House, FS13 - Midtown, FS18 - Retirement Home, FS20 - Parkway Express
 * Each station shows its apparatus (E5##, A5##, M5##, TW5##, etc.)
 * with assignment slots for members.
 */

import { useOnShift } from '../hooks/useOnShift';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { computeBattalionForDate, getTodayDateStr } from '../lib/schedule';
import type { OnShiftMember } from '../hooks/useOnShift';
import AppLayout from '../components/layout/AppLayout';
import { RefreshCw, MapPin, Truck, User, Shield, Clock } from 'lucide-react';

// -- Station definitions --

interface StationDef {
  id: string;
  name: string;
  fullName: string;
  apparatus: string[];
}

const STATIONS: StationDef[] = [
  {
    id: 'FS10',
    name: 'FS10',
    fullName: 'Dime House',
    apparatus: ['E510', 'A510', 'M510', 'R510', 'U510'],
  },
  {
    id: 'FS13',
    name: 'FS13',
    fullName: 'Midtown',
    apparatus: ['E513', 'A513', 'M513', 'TW513', 'U513'],
  },
  {
    id: 'FS18',
    name: 'FS18',
    fullName: 'Retirement Home',
    apparatus: ['E518', 'A518', 'M518', 'B518', 'U518'],
  },
  {
    id: 'FS20',
    name: 'FS20',
    fullName: 'Parkway Express',
    apparatus: ['E520', 'A520', 'M520', 'U520'],
  },
];

// -- Helpers --

function formatName(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length < 2) return displayName;
  const firstInitial = parts[0][0];
  const lastName = parts.slice(1).join(' ');
  return `${firstInitial}. ${lastName}`;
}

function formatPosition(position: string): string {
  if (position === 'aic') return 'AIC';
  return position
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Position sort priority — lower number = displayed first */
const POSITION_SORT_ORDER: Record<string, number> = {
  incident_officer: 0,
  aic: 0,
  battalion_chief: 0,
  safety_officer: 0,
  aerial_operator: 1,
  driver_pump_operator: 1,
  driver_operator: 1,
  operator: 1,
  lead_firefighter: 2,
  firefighter: 3,
  recruit_firefighter: 4,
  aide: 3,
  fist: 5,
};

function positionOrder(position: string | null | undefined): number {
  if (!position) return 99;
  return POSITION_SORT_ORDER[position] ?? 50;
}

function sortMembersByPosition(members: OnShiftMember[]): OnShiftMember[] {
  return [...members].sort((a, b) => positionOrder(a.position) - positionOrder(b.position));
}

/** Unit type colors matching the roster ApparatusView */
function getApparatusColor(id: string): string {
  if (id.startsWith('E')) return '#EF5350';
  if (id.startsWith('A')) return '#42A5F5';
  if (id.startsWith('M')) return '#AB47BC';
  if (id.startsWith('TW')) return '#FFA726';
  if (id.startsWith('R')) return '#66BB6A';
  if (id.startsWith('B')) return '#8D6E63';
  if (id.startsWith('U')) return '#999';
  return '#999';
}

function getApparatusStation(apparatus: string): string {
  const match = apparatus.match(/\d+/);
  if (!match) return '';
  const num = match[0];
  if (num.startsWith('510')) return 'FS10';
  if (num.startsWith('513')) return 'FS13';
  if (num.startsWith('518')) return 'FS18';
  if (num.startsWith('520')) return 'FS20';
  return '';
}

function getApparatusTypeLabel(id: string): string {
  if (id.startsWith('R')) return 'Rescue';
  if (id.startsWith('E')) return 'Engine';
  if (id.startsWith('A')) return 'Ambulance';
  if (id.startsWith('M')) return 'Medic';
  if (id.startsWith('TW')) return 'Tower';
  if (id.startsWith('B')) return 'Brush';
  if (id.startsWith('U')) return 'Utility';
  return 'Unit';
}

interface ApparatusAssignment {
  apparatus: string;
  typeLabel: string;
  members: OnShiftMember[];
}

function buildStationAssignments(stationDef: StationDef, allMembers: OnShiftMember[]): ApparatusAssignment[] {
  const membersByApparatus = new Map<string, OnShiftMember[]>();

  for (const m of allMembers) {
    if (!m.apparatus) continue;
    const stationId = getApparatusStation(m.apparatus);
    if (stationId !== stationDef.id) continue;
    const existing = membersByApparatus.get(m.apparatus) ?? [];
    existing.push(m);
    membersByApparatus.set(m.apparatus, existing);
  }

  return stationDef.apparatus.map((appId) => ({
    apparatus: appId,
    typeLabel: getApparatusTypeLabel(appId),
    members: sortMembersByPosition(membersByApparatus.get(appId) ?? []),
  }));
}

// -- Battalion text colors --

const BATTALION_TEXT_COLORS: Record<number, string> = {
  1: '#4DD0E1',
  2: '#AED581',
  3: '#EF9A9A',
  4: '#FFB74D',
  5: '#9FA8DA',
};

// -- Component --

export default function OnShift() {
  const { members, loading, error, refresh } = useOnShift();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const todayComputed = computeBattalionForDate(getTodayDateStr());

  // Find unassigned members (no apparatus or apparatus doesn't match any station)
  const assignedApparatus = new Set(STATIONS.flatMap((s) => s.apparatus));
  const unassignedMembers = members.filter((m) => {
    if (!m.apparatus) return true;
    return !assignedApparatus.has(m.apparatus);
  });

  if (loading) {
    return (
      <AppLayout title="Who's On Duty">
        <div className="flex items-center justify-center min-h-[60dvh] text-content-tertiary text-base">Loading...</div>
      </AppLayout>
    );
  }

  const bnNum = todayComputed.battalion;
  const bnColor = bnNum ? BATTALION_TEXT_COLORS[bnNum] : undefined;

  return (
    <AppLayout title="Who's On Duty">
      <div className="w-full">
        {/* Header bar */}
        <div className="flex items-center justify-between mt-3 mb-5">
          <div>
            {bnNum != null && (
              <div className="text-sm font-bold uppercase tracking-wide" style={{ color: bnColor }}>
                Battalion {bnNum} On Duty
              </div>
            )}
            <div className="text-xs text-content-tertiary mt-0.5">
              {members.length} member{members.length !== 1 ? 's' : ''} on duty
              {members.some((m) => m.source === 'planned') && (
                <span className="ml-1">
                  ({members.filter((m) => m.source === 'clocked_in').length} clocked in,{' '}
                  {members.filter((m) => m.source === 'planned').length} scheduled)
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-content-secondary
                       bg-surface-raised border border-edge-default rounded-lg cursor-pointer
                       hover:bg-surface-sunken transition-colors duration-150"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>

        {/* Error state */}
        {error && (
          <div className="px-4 py-3 bg-danger-500/15 border border-danger-500/30 rounded-lg text-sm text-danger-400 mb-4">
            {error}
          </div>
        )}

        {/* Shift-wide assignments: Battalion Chief & Safety Officer */}
        <div className={`grid gap-4 mb-4 ${isDesktop ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {['Battalion Chief', 'Safety Officer'].map((role) => {
            // Find member assigned to this role from the members list
            const assignedMember = members.find(
              (m) => m.position?.toLowerCase().replace(/[_\s]+/g, '') === role.toLowerCase().replace(/\s+/g, ''),
            );
            return (
              <div key={role} className="bg-surface-raised rounded-xl border border-edge-default overflow-hidden">
                <div className="flex items-center gap-2.5 px-4 py-3 bg-surface-sunken border-b border-edge-muted">
                  <Shield className="h-4 w-4 text-action-primary shrink-0" />
                  <div className="text-sm font-bold text-content-primary">{role}</div>
                </div>
                <div className="px-4 py-3">
                  {assignedMember ? (
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          assignedMember.source === 'planned'
                            ? 'bg-warning-500/15 border border-dashed border-warning-500/40'
                            : 'bg-action-primary/15'
                        }`}
                      >
                        {assignedMember.source === 'planned' ? (
                          <Clock className="h-3.5 w-3.5 text-warning-500" />
                        ) : (
                          <User className="h-3.5 w-3.5 text-action-primary" />
                        )}
                      </div>
                      <span className="text-sm font-semibold text-content-primary">{assignedMember.display_name}</span>
                      {assignedMember.pwc_username && (
                        <span className="text-[11px] text-content-tertiary">({assignedMember.pwc_username})</span>
                      )}
                      {assignedMember.source === 'planned' && (
                        <span className="text-[10px] font-semibold text-warning-500 bg-warning-500/10 px-1.5 py-0.5 rounded">
                          Scheduled
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-content-tertiary">
                      <div className="w-6 h-6 rounded-full border border-dashed border-edge-muted flex items-center justify-center">
                        <User className="h-3.5 w-3.5 text-content-disabled" />
                      </div>
                      <span className="text-xs italic">Not assigned</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Station cards grid */}
        <div className={`grid gap-4 ${isDesktop ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {STATIONS.map((station) => {
            const assignments = buildStationAssignments(station, members);

            return (
              <div key={station.id} className="bg-surface-raised rounded-xl border border-edge-default overflow-hidden">
                {/* Station header */}
                <div className="flex items-center gap-2.5 px-4 py-3 bg-surface-sunken border-b border-edge-muted">
                  <MapPin className="h-4 w-4 text-action-primary shrink-0" />
                  <div>
                    <div className="text-sm font-bold text-content-primary">
                      {station.name} - {station.fullName}
                    </div>
                  </div>
                </div>

                {/* Apparatus list */}
                <div className="p-3 space-y-2">
                  {assignments.map((unit) => (
                    <div key={unit.apparatus} className="rounded-lg border border-edge-muted overflow-hidden">
                      {/* Apparatus header */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-surface-base">
                        <Truck className="h-3.5 w-3.5 shrink-0" style={{ color: getApparatusColor(unit.apparatus) }} />
                        <span
                          className="text-xs font-bold uppercase tracking-wide"
                          style={{ color: getApparatusColor(unit.apparatus) }}
                        >
                          {unit.apparatus}
                        </span>
                        <span className="text-[10px] text-content-tertiary">{unit.typeLabel}</span>
                      </div>

                      {/* Member assignments */}
                      <div className="px-3 py-2">
                        {unit.members.length > 0 ? (
                          <div className="space-y-1">
                            {unit.members.map((member, idx) => (
                              <div key={idx} className="flex items-center justify-between py-1">
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`w-5 h-5 rounded-full flex items-center justify-center ${
                                      member.source === 'planned'
                                        ? 'bg-warning-500/15 border border-dashed border-warning-500/40'
                                        : 'bg-action-primary/15'
                                    }`}
                                  >
                                    {member.source === 'planned' ? (
                                      <Clock className="h-3 w-3 text-warning-500" />
                                    ) : (
                                      <User className="h-3 w-3 text-action-primary" />
                                    )}
                                  </div>
                                  <span className="text-sm text-content-primary">
                                    {formatName(member.display_name)}
                                  </span>
                                  {member.pwc_username && (
                                    <span className="text-[11px] text-content-tertiary">({member.pwc_username})</span>
                                  )}
                                  {member.source === 'planned' && (
                                    <span className="text-[9px] font-semibold text-warning-500 bg-warning-500/10 px-1 py-0.5 rounded">
                                      Planned
                                    </span>
                                  )}
                                </div>
                                {member.position && (
                                  <span className="text-[10px] font-semibold text-content-secondary bg-surface-sunken px-1.5 py-0.5 rounded">
                                    {formatPosition(member.position)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 py-1.5 text-content-tertiary">
                            <div className="w-5 h-5 rounded-full border border-dashed border-edge-muted flex items-center justify-center">
                              <User className="h-3 w-3 text-content-disabled" />
                            </div>
                            <span className="text-xs italic">No assignment</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Unassigned members */}
        {unassignedMembers.length > 0 && (
          <div className="mt-4 bg-surface-raised rounded-xl border border-edge-default overflow-hidden">
            <div className="px-4 py-3 bg-surface-sunken border-b border-edge-muted">
              <div className="text-sm font-bold text-content-tertiary uppercase tracking-wide">Unassigned</div>
            </div>
            <div className="p-3 space-y-1">
              {unassignedMembers.map((member, idx) => (
                <div key={idx} className="flex items-center justify-between py-1.5 px-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center ${
                        member.source === 'planned'
                          ? 'bg-warning-500/15 border border-dashed border-warning-500/40'
                          : 'bg-surface-sunken'
                      }`}
                    >
                      {member.source === 'planned' ? (
                        <Clock className="h-3 w-3 text-warning-500" />
                      ) : (
                        <User className="h-3 w-3 text-content-tertiary" />
                      )}
                    </div>
                    <span className="text-sm text-content-primary">{formatName(member.display_name)}</span>
                    {member.pwc_username && (
                      <span className="text-[11px] text-content-tertiary">({member.pwc_username})</span>
                    )}
                    {member.source === 'planned' && (
                      <span className="text-[9px] font-semibold text-warning-500 bg-warning-500/10 px-1 py-0.5 rounded">
                        Planned
                      </span>
                    )}
                  </div>
                  <div className="flex gap-1.5 items-center">
                    {member.position && (
                      <span className="text-[10px] font-semibold text-content-secondary bg-surface-sunken px-1.5 py-0.5 rounded">
                        {formatPosition(member.position)}
                      </span>
                    )}
                    {member.apparatus && (
                      <span className="text-[10px] font-semibold text-action-primary bg-action-primary/10 px-1.5 py-0.5 rounded">
                        {member.apparatus}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
